import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];

function getExtension(url) {
  return (url || '').split('?')[0].split('.').pop().toLowerCase();
}

export function isViewableFile(url, label) {
  const extFromUrl = getExtension(url);
  if (extFromUrl === 'pdf' || IMAGE_EXTS.includes(extFromUrl)) return true;
  // Fallback: check the label (Drupal often puts the real filename there)
  const extFromLabel = getExtension(label);
  return extFromLabel === 'pdf' || IMAGE_EXTS.includes(extFromLabel);
}

export function getFileViewerType(url, label) {
  const extFromUrl = getExtension(url);
  if (extFromUrl === 'pdf') return 'pdf';
  if (IMAGE_EXTS.includes(extFromUrl)) return 'image';
  const extFromLabel = getExtension(label);
  if (extFromLabel === 'pdf') return 'pdf';
  if (IMAGE_EXTS.includes(extFromLabel)) return 'image';
  return null;
}

export function FileViewer({ url, filename, onClose }) {
  const shouldReduceMotion = useReducedMotion();
  const [blobUrl, setBlobUrl] = useState(null);
  const [mimeType, setMimeType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setBlobUrl(null);
    setMimeType(null);
    setLoading(true);
    setError(false);

    let revoked = false;
    let objectUrl = null;

    fetch(url, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ct = res.headers.get('Content-Type') || '';
        setMimeType(ct.split(';')[0].trim());
        return res.blob();
      })
      .then((blob) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setLoading(false);
      })
      .catch(() => {
        if (!revoked) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  const type = mimeType?.startsWith('image/') ? 'image'
    : (mimeType === 'application/pdf' || getFileViewerType(url, filename) === 'pdf') ? 'pdf'
    : getFileViewerType(url, filename);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const backdropTransition = { duration: shouldReduceMotion ? 0 : 0.18 };
  const panelTransition = { duration: shouldReduceMotion ? 0 : 0.22, ease: [0.4, 0, 0.2, 1] };

  const viewer = (
    <div className="cat-file-viewer">
      <motion.button
        type="button"
        className="cat-file-viewer__backdrop"
        aria-label="Fermer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={backdropTransition}
        onClick={onClose}
      />
      <motion.div
        className="cat-file-viewer__panel"
        role="dialog"
        aria-modal="true"
        aria-label={filename || 'Visionneuse de fichier'}
        initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.985 }}
        transition={panelTransition}
      >
        <div className="cat-file-viewer__header">
          <span className="cat-file-viewer__filename">{filename || 'Fichier'}</span>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="cat-file-viewer__header-btn"
            title="Télécharger"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </a>
          <button
            type="button"
            className="cat-file-viewer__header-btn"
            aria-label="Fermer"
            onClick={onClose}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="cat-file-viewer__body">
          {loading ? (
            <div className="cat-file-viewer__state">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="cat-file-viewer__spinner-icon"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
              <span>Chargement…</span>
            </div>
          ) : error ? (
            <div className="cat-file-viewer__state">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span>Impossible d'afficher ce fichier.</span>
              <a href={url} target="_blank" rel="noreferrer" className="cat-file-viewer__dl-link">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Télécharger
              </a>
            </div>
          ) : type === 'image' ? (
            <img src={blobUrl} alt={filename || ''} className="cat-file-viewer__img" />
          ) : (
            <iframe src={blobUrl} className="cat-file-viewer__iframe" title={filename || 'Fichier'} />
          )}
        </div>
      </motion.div>
    </div>
  );

  return createPortal(viewer, document.body);
}

export function FileViewerPortal({ open, url, filename, onClose }) {
  return (
    <AnimatePresence>
      {open ? <FileViewer url={url} filename={filename} onClose={onClose} /> : null}
    </AnimatePresence>
  );
}
